import { useEffect, useRef } from 'react';
import Backdrop from '@mui/material/Backdrop';

// ─── Constants ────────────────────────────────────────────────────────────────
const ORANGE      = '#FF5A1F';
const BLUE        = '#6699FF';
const DURATION_MS = 3000;
const COUNT       = 28;
const TRAIL_GAP   = 0.026;
const W           = 260;
const H           = 360;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ].join(',');
}

function bez(t, x0, y0, x1, y1, x2, y2, x3, y3) {
  const m = 1 - t;
  return {
    x: m*m*m*x0 + 3*m*m*t*x1 + 3*m*t*t*x2 + t*t*t*x3,
    y: m*m*m*y0 + 3*m*m*t*y1 + 3*m*t*t*y2 + t*t*t*y3,
  };
}

function sPoint(t) {
  if (t <= 0.5) {
    return bez(
      t / 0.5,
      182, 52,
      45,  52,
      45,  178,
      130, 178,
    );
  }
  return bez(
    (t - 0.5) / 0.5,
    130, 178,
    215, 178,
    215, 308,
    78,  308,
  );
}

function buildSPath(samples = 1200) {
  const pts = Array.from({ length: samples + 1 }, (_, i) => sPoint(i / samples));
  const arcLen = [0];
  let total = 0;
  for (let i = 1; i <= samples; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    total += Math.sqrt(dx * dx + dy * dy);
    arcLen.push(total);
  }
  return { pts, arcLen, total, N: samples };
}

function pointAtFraction(path, f) {
  f = ((f % 1) + 1) % 1;
  const target = f * path.total;
  let lo = 0, hi = path.N;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    path.arcLen[mid] < target ? (lo = mid) : (hi = mid);
  }
  const alpha =
    path.arcLen[hi] > path.arcLen[lo]
      ? (target - path.arcLen[lo]) / (path.arcLen[hi] - path.arcLen[lo])
      : 0;
  return {
    x: path.pts[lo].x + alpha * (path.pts[hi].x - path.pts[lo].x),
    y: path.pts[lo].y + alpha * (path.pts[hi].y - path.pts[lo].y),
  };
}

// ─── Drawing ──────────────────────────────────────────────────────────────────
function drawTrack(ctx) {
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth   = 2;
  ctx.lineCap     = 'round';
  ctx.lineJoin    = 'round';
  ctx.beginPath();
  ctx.moveTo(182, 52);
  ctx.bezierCurveTo(45,  52,  45,  178, 130, 178);
  ctx.bezierCurveTo(215, 178, 215, 308, 78,  308);
  ctx.stroke();
  ctx.restore();
}

function drawDot(ctx, x, y, r, rgb, alpha, isLead) {
  const glowR = isLead ? r * 6 : r * 3.2;
  const glow  = ctx.createRadialGradient(x, y, r * 0.3, x, y, glowR);
  glow.addColorStop(0, `rgba(${rgb},${(alpha * (isLead ? 0.6 : 0.25)).toFixed(2)})`);
  glow.addColorStop(1, `rgba(${rgb},0)`);
  ctx.beginPath();
  ctx.arc(x, y, glowR, 0, Math.PI * 2);
  ctx.fillStyle = glow;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(${rgb},${alpha.toFixed(2)})`;
  ctx.fill();

  if (r > 2) {
    const hl = ctx.createRadialGradient(x, y, 0, x, y, r * 0.55);
    hl.addColorStop(0, `rgba(255,255,255,${(alpha * 0.55).toFixed(2)})`);
    hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.beginPath();
    ctx.arc(x, y, r * 0.55, 0, Math.PI * 2);
    ctx.fillStyle = hl;
    ctx.fill();
  }
}

// ─── Pre-built path ───────────────────────────────────────────────────────────
const S_PATH     = buildSPath();
const ORANGE_RGB = hexToRgb(ORANGE);
const BLUE_RGB   = hexToRgb(BLUE);

// ─── SandhiLoader ─────────────────────────────────────────────────────────────
function SandhiLoader() {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let startTime = null;

    function frame(ts) {
      if (!startTime) startTime = ts;
      const progress = ((ts - startTime) % DURATION_MS) / DURATION_MS;

      ctx.clearRect(0, 0, W, H);
      drawTrack(ctx);

      for (let i = COUNT - 1; i >= 0; i--) {
        const isLead = i === 0;
        const ratio  = 1 - i / COUNT;
        const r      = isLead ? 9 : 2.5 + ratio * 5.5;
        const alpha  = isLead ? 0.98 : 0.25 + ratio * 0.65;

        const blueFrac  = ((progress - i * TRAIL_GAP) + 100) % 1;
        const bluePos   = pointAtFraction(S_PATH, blueFrac);
        drawDot(ctx, bluePos.x, bluePos.y, r, BLUE_RGB, alpha, isLead);

        const orangeFrac = ((1 - progress + i * TRAIL_GAP) + 100) % 1;
        const orangePos  = pointAtFraction(S_PATH, orangeFrac);
        drawDot(ctx, orangePos.x, orangePos.y, r, ORANGE_RGB, alpha, isLead);
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={W}
      height={H}
      style={{ display: 'block' }}
    />
  );
}

// ─── LoaderComponent ──────────────────────────────────────────────────────────
function LoaderComponent({ loaderOpen }) {
  return (
    <Backdrop
      open={loaderOpen}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        // ✅ Semi-transparent dark overlay — lets your screen show through dimmed
        // Adjust the last value (0.45) to taste: 0 = fully transparent, 1 = solid black
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <SandhiLoader />
    </Backdrop>
  );
}

export default LoaderComponent;